import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../configureStore";
import { setFieldInvalid } from "../redux/reducer/returns";
import { FormConfig } from "../components/organisms/TaxReturnWebforms/types/formConfig";
import {
    buildInvalidKey,
    collectDateValidationFields,
    getValueAtPath,
    isMmDdYyyyInvalid,
} from "../components/organisms/TaxReturnWebforms/shared/collectDateValidationFields";

/**
 * Walks every loaded form config and validates each `checkDateFormat: true` field against
 * the corresponding extraction data. Runs whenever the loaded config set or extracted JSON
 * changes, so errors are surfaced across every page of every form in the extract task —
 * including pages the user has not navigated to yet (as long as that form's config has loaded).
 */
export const useDateValidationScan = () => {
    const dispatch = useDispatch<AppDispatch>();
    const extractedJson = useSelector(({ returns }: RootState) => returns.extractedJson);
    const extractionConfigs = useSelector(
        ({ configs }: RootState) => configs.extractionConfigs as Record<string, FormConfig>,
    );

    useEffect(() => {
        if (!extractedJson) return;

        // Index configs by formType, preferring year-specific over default for each formType.
        const configByFormType = new Map<string, FormConfig>();
        for (const [name, cfg] of Object.entries(extractionConfigs)) {
            if (!cfg) continue;
            const formType = cfg.formType ?? name.replace(/-(\d{4}|default)$/, "");
            const existing = configByFormType.get(formType);
            // Prefer year-specific (anything not `-default`) over the default config.
            if (!existing || name.endsWith("-default") === false) {
                configByFormType.set(formType, cfg);
            }
        }

        for (const [formType, formExtraction] of Object.entries(extractedJson)) {
            const cfg = configByFormType.get(formType);
            if (!cfg || !formExtraction) continue;
            const fields = collectDateValidationFields(cfg);
            if (fields.length === 0) continue;

            const extractions = (formExtraction as { extraction?: any[] }).extraction;
            if (!Array.isArray(extractions)) continue;

            for (const extraction of extractions) {
                const data = extraction?.extractedData?.data;
                const pageNumber = extraction?.extractedData?.formPageNumber ?? extraction?.pageNumber;
                if (!data || pageNumber == null) continue;
                for (const f of fields) {
                    if (f.pageNumber !== pageNumber) continue;
                    const value = getValueAtPath(data, f.fieldPath);
                    const invalid = isMmDdYyyyInvalid(value);
                    const key = buildInvalidKey(formType, f.pageNumber, f.fieldPath);
                    dispatch(
                        setFieldInvalid(key, invalid, {
                            formType,
                            formName: cfg.formName,
                            pageNumber: f.pageNumber,
                        }),
                    );
                }
            }
        }
    }, [dispatch, extractedJson, extractionConfigs]);
};
